import { FaWhatsapp } from "react-icons/fa6";
import { FaPhoneAlt } from "react-icons/fa";
import styles from "./seller-contact-dialog.module.css";
import { MdChat, MdClose } from "react-icons/md";
import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { CircleFlag } from "react-circle-flags";
import { IoIosArrowDown } from "react-icons/io";
import {
  getCountryFromPrefix,
  phonePrefixes,
} from "@/lib/phone-prefixes/prefixes";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { ContactDialogType, UserTypes } from "@/types/types";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { listingContactHandler } from "./handlers";
import { listingContactSchema, listingContactSchemaAuth } from "./schemas";
import { toast } from "react-toastify";

export default function ListingContactDialog({
  userSub,
  photoUrl,
  userName,
  userType,
  closeCallback,
  phoneNumber,
  hasWhatsAppActive,
  dialogRef,
  listingId,
  activeType,
  setActiveType,
  isComponent = false,
  isPriceRequest = false,
}: {
  userSub: string;
  photoUrl: string;
  userName: string;
  userType: UserTypes;
  closeCallback: () => void;
  phoneNumber: string | null;
  hasWhatsAppActive: boolean | null;
  dialogRef: RefObject<HTMLDivElement | null>;
  listingId: string;
  activeType: ContactDialogType;
  setActiveType: (newType: ContactDialogType) => void;
  isComponent?: boolean;
  isPriceRequest?: boolean;
}) {
  const t = useTranslations("ContactDialog.listing");
  const [isPhoneNumberActive, setIsPhoneNumberActive] = useState(false);
  const [isPhonePrefixOpen, setIsPhonePrefixOpen] = useState(false);
  const locale = useLocale();
  const [numberSearchQuery, setNumberSearchQuery] = useState("");
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const filteredPrefixes = phonePrefixes.filter((country) =>
    (displayNames.of(country.code) ?? "")
      .toLowerCase()
      .includes(numberSearchQuery.toLowerCase()),
  );
  const firstName = userName.split(" ")[0] ?? "";
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [activePrefix, setActivePrefix] = useState("");
  const [message, setMessage] = useState(
    isPriceRequest
      ? t("priceRequestPlaceholderMessage", { userName: firstName })
      : t("placeholderMessage", { userName: firstName }),
  );
  const { userData } = useUserAuthDataContext();

  return (
    <div
      className={`${styles.dialogWrapper} ${
        isComponent ? styles.isComponent : ""
      }`}
      onClick={(e) => {
        if (!isComponent) {
          e.stopPropagation();
          closeCallback();
        }
      }}
    >
      <div
        className={styles.dialogContent}
        ref={dialogRef}
        onClick={(e) => !isComponent && e.stopPropagation()}
      >
        <div className={styles.closeButtonWrapper}>
          <span className={styles.title}>{t("titleMessage")}</span>
          {!isComponent && (
            <button
              className={styles.closeButton}
              onClick={(e) => {
                e.preventDefault();
                closeCallback();
              }}
            >
              <MdClose className={styles.closeIcon} />
            </button>
          )}
        </div>
        <div className={styles.agentDetails}>
          <Link
            className={`${styles.imageLink} ${styles.imageLinkDesktop}`}
            target="_blank"
            rel="noopener noreferrer"
            href={makeLocalizedHrefNoLocale(locale, "profiles", [`${userSub}`])}
          >
            <Image
              className={styles.agentImage}
              src={photoUrl}
              width={128}
              height={128}
              alt="User uploaded profile photo"
            />
          </Link>
          <p className={styles.userName}>{userName}</p>
        </div>
        <div className={styles.mainContentWrapper}>
          <div className={styles.selector}>
            <button
              onClick={(e) => {
                e.preventDefault();
                setActiveType(ContactDialogType.LISTING);
              }}
              className={`${
                activeType === ContactDialogType.LISTING && styles.isActive
              }`}
            >
              {t("message")}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                setActiveType(ContactDialogType.TOUR);
              }}
              className={`${
                activeType === ContactDialogType.TOUR && styles.isActive
              }`}
            >
              {t("requestTour")}
            </button>
          </div>
          <span className={styles.description}>
            {!userData.idToken ? t("contactInfo") : t("enterMessage")}
          </span>
          {!userData.idToken || userType === UserTypes.AGENCY ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const validation = listingContactSchema.safeParse({
                  fullName,
                  email,
                  phoneNumber: `${activePrefix + phoneNumberInput}`,
                  message,
                  listingId,
                });
                if (!validation.success) {
                  const firstError = validation.error.errors[0]?.message;
                  toast(t(firstError), { type: "error" });
                  return;
                }
                const isSuccess = await listingContactHandler(
                  userSub,
                  userType,
                  fullName,
                  email,
                  `${activePrefix + phoneNumberInput}`,
                  message,
                  listingId,
                  userData.idToken,
                  userData.sub,
                );
                if (isSuccess) {
                  setActivePrefix("");
                  setEmail("");
                  setFullName("");
                  setMessage("");
                  setPhoneNumberInput("");
                  closeCallback();
                }
              }}
              className={styles.contactForm}
            >
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="text"
                  placeholder={t("completeName")}
                  value={fullName}
                  onChange={(e) => {
                    e.preventDefault();
                    setFullName(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="email"
                  placeholder={t("email")}
                  value={email}
                  onChange={(e) => {
                    e.preventDefault();
                    setEmail(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <div className={styles.phoneNumberWrapper}>
                  <button
                    className={styles.prefixButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPhonePrefixOpen(!isPhonePrefixOpen);
                      setNumberSearchQuery("");
                    }}
                  >
                    {activePrefix && (
                      <span>
                        <CircleFlag
                          countryCode={getCountryFromPrefix(
                            activePrefix,
                          ).toLocaleLowerCase()}
                          className={styles.countryIcon}
                        />
                      </span>
                    )}
                    {activePrefix == "" ? t("none") : activePrefix}
                    <span>
                      <IoIosArrowDown className={styles.arrowIcon} />
                    </span>
                  </button>
                  <input
                    className={styles.phoneInput}
                    type="number"
                    placeholder="797 285 954"
                    value={phoneNumberInput}
                    onChange={(e) => {
                      e.preventDefault();
                      setPhoneNumberInput(e.target.value);
                    }}
                  />
                  {isPhonePrefixOpen && (
                    <div className={styles.phonePrefixesWrapper}>
                      <input
                        className={styles.phonePrefixInput}
                        type="text"
                        placeholder={t("phoneCountryPlaceholder")}
                        onChange={(e) => {
                          e.preventDefault();
                          setNumberSearchQuery(e.target.value);
                        }}
                      />
                      <ul>
                        {filteredPrefixes.map((activePrefix, index) => {
                          return (
                            <li key={index}>
                              <button
                                className={styles.prefixListButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActivePrefix(activePrefix.dial_code);
                                  setIsPhonePrefixOpen(false);
                                  setNumberSearchQuery("");
                                }}
                              >
                                <span>
                                  <CircleFlag
                                    countryCode={activePrefix.code.toLowerCase()}
                                    className={styles.countryIcon}
                                  />
                                </span>
                                <span>{activePrefix.dial_code}</span>
                                <span className={styles.phoneCountryText}>
                                  {displayNames.of(activePrefix.code) ?? ""}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <textarea
                  className={styles.messageTextArea}
                  placeholder={t("enterMessage")}
                  value={message}
                  maxLength={400}
                  onChange={(e) => {
                    e.preventDefault();
                    setMessage(e.target.value);
                  }}
                />
              </div>
              <button type="submit" className={styles.sendButton}>
                {t("sendMessage")}
              </button>
            </form>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const validation = listingContactSchemaAuth.safeParse({
                  message,
                  listingId,
                });
                if (!validation.success) {
                  const firstError = validation.error.errors[0]?.message;
                  toast(t(firstError), { type: "error" });
                  return;
                }
                const isSuccess = await listingContactHandler(
                  userSub,
                  userType,
                  fullName,
                  email,
                  `${activePrefix + phoneNumberInput}`,
                  message,
                  listingId,
                  userData.idToken,
                  userData.sub,
                );
                if (isSuccess) {
                  setActivePrefix("");
                  setEmail("");
                  setFullName("");
                  setMessage("");
                  setPhoneNumberInput("");
                  closeCallback();
                }
              }}
              className={styles.contactForm}
            >
              <div className={`${styles.fieldWrapper}`}>
                <textarea
                  className={styles.messageTextArea}
                  placeholder={t("enterMessage")}
                  value={message}
                  maxLength={400}
                  onChange={(e) => {
                    e.preventDefault();
                    setMessage(e.target.value);
                  }}
                />
              </div>
              <button type="submit" className={styles.sendButton}>
                {t("sendMessage")}
              </button>
            </form>
          )}
        </div>
        {phoneNumber !== null && <span className={styles.or}>{t("or")}</span>}
        {phoneNumber !== null && (
          <div className={styles.buttonsWrapper}>
            {!isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={(e) => {
                  e.preventDefault();
                  setIsPhoneNumberActive(true);
                }}
              >
                <span>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                </span>
                {t("showNumber")}
              </button>
            )}
            {phoneNumber !== null && isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={async (e) => {
                  e.preventDefault();

                  // 1. Ensure the value is a string, otherwise writeText crashes
                  const textToCopy = phoneNumber ? String(phoneNumber) : "";

                  if (!textToCopy) {
                    toast(t("copyFailed"), { type: "error" });
                    return;
                  }

                  // 2. Try the modern Clipboard API first (Requires HTTPS or Localhost)
                  if (navigator.clipboard && window.isSecureContext) {
                    try {
                      await navigator.clipboard.writeText(textToCopy);
                      toast(t("phoneCopied"), { type: "success" });
                      return; // Exit if successful
                    } catch (error) {
                      console.warn(
                        "Modern clipboard API failed, trying fallback...",
                        error,
                      );
                    }
                  }

                  // 3. The Bulletproof Fallback (Works on HTTP, older devices, and iOS WebViews)
                  try {
                    const textArea = document.createElement("textarea");
                    textArea.value = textToCopy;

                    // Hide the textarea entirely off-screen
                    textArea.style.position = "absolute";
                    textArea.style.left = "-999999px";

                    document.body.appendChild(textArea);

                    // Special selection logic to make iOS Safari happy
                    if (navigator.userAgent.match(/ipad|iphone/i)) {
                      const range = document.createRange();
                      range.selectNodeContents(textArea);
                      const selection = window.getSelection();
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                      textArea.setSelectionRange(0, 999999);
                    } else {
                      textArea.select();
                    }

                    document.execCommand("copy");
                    textArea.remove();

                    toast(t("phoneCopied"), { type: "success" });
                  } catch (fallbackError) {
                    console.error(
                      "Fallback clipboard also failed:",
                      fallbackError,
                    );
                    toast(t("copyFailed"), { type: "error" });
                  }
                }}
                aria-label={t("copyPhoneNumber")}
              >
                <span className={styles.phoneText}>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                  <span>&lrm;{phoneNumber}</span>
                </span>
              </button>
            )}
            {hasWhatsAppActive && (
              <a
                className={`${styles.button} ${styles.whatsAppButton}`}
                target="_blank"
                rel="noopener noreferrer"
                href={`https://wa.me/${formatPhoneForWhatsApp(phoneNumber)}`}
              >
                <span>
                  <FaWhatsapp className={styles.buttonIcon} />
                </span>
                {t("whatsApp")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatPhoneForWhatsApp(phone: string): string {
  return phone.replace(/[^0-9]/g, ""); // Keep only digits
}
